import { AssetManager } from '@/assets/AssetManager';
import { AudioEngine } from '@/engine/AudioEngine';
import { Recorder } from '@/engine/Recorder';
import { MidiInput } from '@/engine/MidiInput';
import { IndexedDBStorage } from '@/storage/IndexedDBStorage';
import type { StorageProvider } from '@/storage/StorageProvider';
import { AutosaveService } from '@/storage/autosave';
import { CommandStack } from '@/commands/CommandStack';
import { PlayheadClock } from './playhead';

/**
 * Long-lived services.
 *
 * These are created once and outlive every React tree. Keeping them out of
 * component state is what lets the audio engine survive a hot reload, a route
 * change or a re-render without dropping the AudioContext — recreating an
 * AudioContext costs a device round trip and, on some hardware, an audible gap.
 *
 * The storage field is typed as the `StorageProvider` interface, not as the
 * IndexedDB class, so that adding a cloud provider later is a one-line change
 * here rather than a rewrite everywhere.
 */

export interface Services {
  storage: StorageProvider;
  /**
   * The asset pipeline, built on the first call and alive from then on.
   *
   * Its context is an `OfflineAudioContext`, which needs no user gesture and is
   * only ever asked to decode. That is what lets a project's stretches and
   * transpositions be rendered while the browser is still waiting for a click.
   *
   * A function rather than a field because a Web Audio object must not be built
   * while this module is being imported: nothing has had a chance to install a
   * feature test, a server rendering the page has no such constructor at all,
   * and a test has not yet put its stubs in place.
   */
  assetPipeline: () => AssetManager;
  engine: AudioEngine;
  commands: CommandStack;
  autosave: AutosaveService;
  playhead: PlayheadClock;
  recorder: () => Recorder | null;
  assets: () => AssetManager | null;
  midi: MidiInput;
}

let services: Services | null = null;
let recorder: Recorder | null = null;
let midi: MidiInput | null = null;
let pipeline: AssetManager | null = null;

/**
 * The decoding pipeline, made once.
 *
 * One frame at a common rate: nothing is played through this context, so its
 * length and sample rate are immaterial — `decodeAudioData` resamples to the
 * context's rate, and every consumer wraps raw samples in a buffer of its own.
 * What matters is that it exists without a user gesture, which is what lets a
 * project be prepared before anybody has clicked anything.
 */
function ensureAssetPipeline(engine: AudioEngine, storage: StorageProvider): AssetManager {
  if (!pipeline) {
    pipeline = new AssetManager(new OfflineAudioContext(2, 1, 48000), storage);
    engine.useAssets(pipeline);
  }
  return pipeline;
}

export interface ServiceOptions {
  onAutosaveError?: (error: unknown) => void;
  onAutosave?: (savedAt: number) => void;
}

export function getServices(options: ServiceOptions = {}): Services {
  if (services) return services;

  const storage = new IndexedDBStorage();
  const engine = new AudioEngine();
  const commands = new CommandStack();
  const autosave = new AutosaveService(storage, {
    onError: options.onAutosaveError,
    onSnapshot: options.onAutosave,
  });
  const playhead = new PlayheadClock(
    () => engine.currentTicks(),
    () => engine.currentSeconds(),
  );

  midi = new MidiInput();
  services = {
    storage,
    assetPipeline: () => ensureAssetPipeline(engine, storage),
    engine,
    midi,
    commands,
    autosave,
    playhead,
    assets: () => engine.assetManager,
    recorder: () => recorder,
  };
  return services;
}

/**
 * Creates the AudioContext, which is the one thing that does need a gesture.
 *
 * Everything else — decoding, waveforms, stretching, transposing — has been
 * running since the app loaded.
 */
export async function ensureAudio(): Promise<AudioEngine> {
  const { engine } = getServices();
  await engine.initialise();
  const ctx = engine.context;
  if (ctx && !recorder) recorder = new Recorder(ctx);
  return engine;
}

export async function disposeServices(): Promise<void> {
  await recorder?.close();
  recorder = null;
  midi?.close();
  midi = null;
  pipeline = null;
  services?.autosave.dispose();
  services?.playhead.dispose();
  await services?.engine.dispose();
  services = null;
}
